import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { differenceInMinutes } from "date-fns";
import { useMemo } from "react";

import { TimeRangeLabel } from "../../DateTime/TimeRangeLabel";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../../Shift/constants";
import { formatBreakDuration } from "../../utils/formatBreakDuration";

export interface ShiftTimeDetailsProps {
  shiftStartDate: Date;
  shiftEndDate: Date;
  workplaceTimezone: string;
  isVerified: boolean;
  verifiedBreakDurationInMinutes: number;
  workplaceBreakDurationInMinutes: number;
}

/**
 * Formats break info specifically for shift history cards.
 * - For verified shifts: shows actual break taken from lunchInOut, or "no break taken" if none
 * - For unverified shifts: shows expected mandatory break based on facility config and shift duration
 */
function formatShiftHistoryBreakInfo(params: {
  isVerified: boolean;
  verifiedBreakDurationInMinutes: number;
  workplaceBreakDurationInMinutes: number;
  totalDurationInHours: number;
}): string {
  const {
    isVerified,
    verifiedBreakDurationInMinutes,
    workplaceBreakDurationInMinutes,
    totalDurationInHours,
  } = params;

  if (isVerified) {
    if (verifiedBreakDurationInMinutes > 0) {
      const breakFormatted = formatBreakDuration(verifiedBreakDurationInMinutes);
      return `incl. ${breakFormatted} unpaid break`;
    }
    return "no break taken";
  }

  const isEligibleForMandatoryBreak =
    totalDurationInHours >= MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;

  if (isEligibleForMandatoryBreak && workplaceBreakDurationInMinutes > 0) {
    const breakFormatted = formatBreakDuration(workplaceBreakDurationInMinutes);
    return `incl. ${breakFormatted} unpaid break`;
  }

  return "no mandatory break";
}

/**
 * Formats shift duration as "Xh" or "Xh YYm"
 */
function formatShiftDuration(totalDurationInHours: number): string {
  const totalDurationInMinutes = Math.round(totalDurationInHours * 60);
  const totalHours = Math.floor(totalDurationInMinutes / 60);
  const totalMinutes = totalDurationInMinutes % 60;

  let duration = `${totalHours}h`;
  if (totalMinutes > 0) {
    duration += ` ${totalMinutes.toString().padStart(2, "0")}m`;
  }
  return duration;
}

export function ShiftTimeDetails(props: ShiftTimeDetailsProps) {
  const {
    shiftStartDate,
    shiftEndDate,
    workplaceTimezone,
    isVerified,
    verifiedBreakDurationInMinutes,
    workplaceBreakDurationInMinutes,
  } = props;

  const deviceTimezone = useMemo(() => new Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const totalDurationInHours = useMemo(
    () => differenceInMinutes(shiftEndDate, shiftStartDate) / 60,
    [shiftStartDate, shiftEndDate]
  );

  const duration = useMemo(() => formatShiftDuration(totalDurationInHours), [totalDurationInHours]);

  const breakInfo = useMemo(
    () =>
      formatShiftHistoryBreakInfo({
        isVerified,
        verifiedBreakDurationInMinutes,
        workplaceBreakDurationInMinutes,
        totalDurationInHours,
      }),
    [
      isVerified,
      verifiedBreakDurationInMinutes,
      workplaceBreakDurationInMinutes,
      totalDurationInHours,
    ]
  );

  return (
    <Stack spacing={1}>
      <TimeRangeLabel
        semibold
        dateRange={{
          startDate: shiftStartDate,
          endDate: shiftEndDate,
        }}
        timezone={workplaceTimezone}
        deviceTimezone={deviceTimezone}
        variant="h5"
      />
      <Stack direction="row" spacing={2} alignItems="baseline">
        <Text variant="body1">{duration}</Text>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
          {breakInfo}
        </Text>
      </Stack>
    </Stack>
  );
}
